import React from "react";
import { StyleSheet, View, TouchableOpacity } from "react-native";
import { useNavigation } from "@react-navigation/native";
import { useDispatch } from "react-redux";
import Text from "@components/AppText";
import colors from "@constants/color";
import HorizontalSeperator from "@assets/svg/HorizontalSeperator";
import { applyCouponCode } from "@services/api";
import { actions } from "@reducers/cartReducer";
import analytics from "@services/analytics";
import metrics from "@constants/metrics";
import Caret from "@assets/svg/Caret";
import t from "@constants/localization";
import { COUPON_FLOW_EVENTS } from "../../constants/analyticsEventsCT";

const PATH = "screens/CouponScreen/AvailableCoupon";

const COUPON_INFO_HEIGHT = 40;
const DOT_DIMENSION = 15;
const DOT_DIMENSION_HALF = DOT_DIMENSION / 2;

const AvailableCoupon = ({
  visibleInModal = false,
  handleActiveCoupon = () => {},
  coupon = {},
  handleToggleLoading = () => {},
  userId = "",
  dontApplyCoupon = false,
  cart_items,
  cart_value,
}) => {
  const {
    coupon_title = "",
    coupon_description = "",
    coupon_code = "",
    percentage_value = "",
    valid_to = "",
    id = "",
    coupon_terms = [],
    is_applicable = false,
    invitation_message = "",
    max_value = "",
  } = coupon || {};

  const navigation = useNavigation();
  const dispatch = useDispatch();

  const validTill = valid_to ? new Date(valid_to).toDateString().slice(4) : "";

  const handleApplyCoupon = () => {
    handleToggleLoading(true);
    // Apply here at the same moment and then dispatch an action

    applyCouponCode({ couponCode: coupon_code, user_id: userId })
      .then(() => {
        analytics.trackEvent(COUPON_FLOW_EVENTS.COUPON_SELECTED, {
          coupon: coupon_code,
          user_id: userId,
          cart_items,
          cart_value,
          source: "Selection",
        });
        dispatch(actions.toggleCartFetching());
        dispatch(actions.getAllCartItems(userId));
        handleToggleLoading(false);
        navigation.goBack();
      })
      .catch((e) => {
        handleToggleLoading(false);
        console.log(e);
      });
  };

  const MainComponentName = visibleInModal ? View : TouchableOpacity;

  return (
    <View
      style={styles.container}
      onPress={() => {
        handleApplyCoupon();
      }}
    >
      <View style={styles.headerContainer}>
        <View>
          <Text bold fontType="head3">
            {coupon_title}
          </Text>
        </View>
        {!visibleInModal &&
          Array.isArray(coupon_terms) &&
          coupon_terms.length > 0 && (
            <TouchableOpacity
              style={styles.seeDetailsContainer}
              onPress={() => handleActiveCoupon(id)}
            >
              <Text
                bold
                fontType="head3"
                style={{
                  color: colors.primary,
                }}
              >
                {t(`${PATH}/viewDetail`)}
              </Text>
              <Caret strokeColor={colors.primary} />
            </TouchableOpacity>
          )}
      </View>

      <View
        style={[
          styles.beLikeRow,
          {
            marginVertical: 10,
            justifyContent: "space-between",
            width: "100%",
          },
        ]}
      >
        <View
          style={[
            styles.couponContainer,
            {
              borderColor: is_applicable ? colors.primary : colors.lightBlack,
            },
          ]}
        >
          <Text
            fontType="head3"
            bold
            style={{
              color: "#000",
              marginRight: 10,
            }}
          >
            {Number(percentage_value).toFixed(0) === "100"
              ? `₹ ${Number(max_value).toFixed(0)}`
              : `${Number(percentage_value).toFixed(0)}%`}
            <Text fontType="content1"> OFF</Text>
          </Text>
          <View
            style={[
              styles.dashedLine,
              {
                borderColor: is_applicable ? colors.primary : colors.lightBlack,
              },
            ]}
          />
          <Text
            fontType="content2"
            style={{
              color: "#000",
              marginLeft: 10,
            }}
          >
            {coupon_code}
          </Text>
          <View
            style={[
              styles.sideCircle,
              {
                left: -DOT_DIMENSION_HALF,
                borderColor: is_applicable ? colors.primary : colors.lightBlack,
              },
            ]}
          />
          <View
            style={[
              styles.hideHalfCircle,
              {
                left: -DOT_DIMENSION_HALF * 2,
                borderColor: is_applicable ? colors.primary : colors.lightBlack,
              },
            ]}
          />
          <View
            style={[
              styles.sideCircle,
              {
                right: -DOT_DIMENSION_HALF,
                borderColor: is_applicable ? colors.primary : colors.lightBlack,
              },
            ]}
          />
          <View
            style={[
              styles.hideHalfCircle,
              {
                right: -DOT_DIMENSION_HALF * 2,
                borderColor: is_applicable ? colors.primary : colors.lightBlack,
              },
            ]}
          />
        </View>
        {!dontApplyCoupon && is_applicable && (
          <TouchableOpacity
            style={styles.applyButton}
            onPress={is_applicable ? handleApplyCoupon : () => {}}
            disabled={!is_applicable}
          >
            <Text
              fontType="head2"
              style={{
                color: "#fff",
              }}
            >
              {t(`${PATH}/apply`)}
            </Text>
          </TouchableOpacity>
        )}
        {(dontApplyCoupon || !is_applicable) && (
          <Text fontType="head3" bold style={{ color: colors.lightBlack }}>
            {t(`${PATH}/couponNotAvailable`)}
          </Text>
        )}
      </View>

      {!visibleInModal &&
        Boolean(coupon_description) &&
        Array.isArray(coupon_terms) &&
        coupon_terms.length > 0 && (
          <View style={{ marginVertical: 5 }}>
            <Text
              style={{
                lineHeight: 20,
              }}
            >
              {coupon_description}
            </Text>
          </View>
        )}

      {!visibleInModal && (
        <View style={{ marginVertical: 5 }}>
          <Text>
            <Text bold>{validTill} </Text>
            {t(`${PATH}/validUpTo`)}
          </Text>
        </View>
      )}

      {!visibleInModal && !is_applicable && Boolean(invitation_message) && (
        <View style={{ marginVertical: 5 }}>
          <Text style={{ color: "#FF2E00" }}>{invitation_message}</Text>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  headerContainer: {
    // width: '90%',
    flexDirection: "row",
    justifyContent: "space-between",
  },
  container: {
    width: "100%",
    padding: metrics.baseGutter,
    backgroundColor: "#fff",
    borderBottomColor: "#ddd",
    elevation: 3,
    borderRadius: 10,
    marginBottom: metrics.baseGutter,
  },
  beLikeRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  dashedLine: {
    width: 1,
    opacity: 0.7,
    height: 20,
    borderWidth: 0.5,
    borderColor: colors.divider,
    borderStyle: "dashed",
    borderRadius: 1,
  },
  box: {
    height: 40,
    width: 40,
    backgroundColor: "#ddd",
    borderRadius: 5,
  },
  couponContainer: {
    // backgroundColor: colors.primary,
    borderRadius: 5,
    minWidth: "50%",
    maxWidth: "70%",
    padding: 10,
    paddingHorizontal: 20,
    height: COUPON_INFO_HEIGHT,
    borderWidth: 1,
    flexDirection: "row",
    alignItems: "center",
  },
  sideCircle: {
    height: DOT_DIMENSION,
    width: DOT_DIMENSION,
    borderRadius: DOT_DIMENSION_HALF,
    backgroundColor: "#fff",
    borderColor: colors.primary,
    borderWidth: 1,
    position: "absolute",
    top: COUPON_INFO_HEIGHT / 2 - DOT_DIMENSION_HALF,
    // zIndex: 10,
  },
  hideHalfCircle: {
    position: "absolute",
    top: COUPON_INFO_HEIGHT / 2 - DOT_DIMENSION_HALF,
    height: DOT_DIMENSION,
    width: DOT_DIMENSION,
    backgroundColor: "#fff",
  },
  seeDetailsContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  applyButton: {
    // width: '30%',
    // padding: 10,
    height: COUPON_INFO_HEIGHT,
    paddingHorizontal: 15,
    justifyContent: "center",
    backgroundColor: colors.primary,
    borderRadius: 6,
  },
});

export default AvailableCoupon;
